import React, { useState } from 'react';

import { Field } from 'formik';

import { toFullPrecisionString, truncateDecimals } from 'Helpers/formatService';
import { reconcileMilestoneValue } from 'Helpers/procurementUtils';

import DatePickerField from 'CommonComponents/DatePickerField';
import NumberFormat from 'CommonComponents/LocalisedNumberFormat';
import Slider from 'CommonComponents/slider/Slider';

// Closes on a scroll anywhere outside the calendar. A document scroll has the document as its target, which is not an
// Element, so it keeps the calendar open: the input and the portalled calendar move together and a calendar taller than
// a short window is reached by scrolling the page.
const closeOnContainerScroll = (event) => event.target instanceof Element && !event.target.closest('.react-datepicker-popper');

export default function MilestoneRow({
    values,
    errors,
    touched,
    setFieldValue,
    minDate,
    maxDate,
    section,
    index,
    actions,
    showActions = true,
    totalValue = 0,
    provided,
    isSliderAllowed = true,
}) {
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const maxPerc = Number(values?.dates[index]?.maxPercentage ?? 100);
    // Derived from the section total and this row's share rather than read straight out of form
    // state, so the cell can never paint the figure that belonged to the previous total. A value
    // the user typed is kept as typed — see reconcileMilestoneValue.
    const rowPerc = parseFloat(values?.dates[index]?.percOriginal ?? values?.dates[index]?.perc ?? 0);
    const computedAmount = truncateDecimals(
        reconcileMilestoneValue(values?.dates[index]?.valueOriginal ?? values?.dates[index]?.value, totalValue, isNaN(rowPerc) ? 0 : rowPerc),
        2,
    );

    const hasTotalValue = Number(totalValue) > 0;

    // react-number-format hands back the parsed number itself, read with the separators the
    // input was configured with. `source` is 'prop' when the re-render drives the value, and
    // acting on that would feed the field back into itself. A cleared field reports undefined.
    const handleTypedNumber = (handler) => (numberValues, sourceInfo) => {
        if (sourceInfo?.source !== 'event') {
            return;
        }

        handler(numberValues?.floatValue ?? 0);
    };

    const handlePercentChange = (value) => {
        let numericValue = isNaN(value) ? 0 : value;
        if (numericValue > maxPerc) {
            numericValue = maxPerc;
        }

        const truncatedPerc = truncateDecimals(numericValue, 2);

        setFieldValue(`dates.${index}.percOriginal`, toFullPrecisionString(numericValue));
        setFieldValue(`dates.${index}.perc`, truncatedPerc);
        if (hasTotalValue) {
            const newValueOriginal = (totalValue * numericValue) / 100;
            setFieldValue(`dates.${index}.valueOriginal`, toFullPrecisionString(newValueOriginal));
            setFieldValue(`dates.${index}.value`, truncateDecimals(newValueOriginal, 2));
        }

        setFieldValue(`dates.${index}.isUserEdited`, true);
    };

    const handleValueChange = (value) => {
        let vOriginal = isNaN(value) ? 0 : value;
        if (hasTotalValue) {
            const maxValue = (maxPerc / 100) * totalValue;
            if (vOriginal > maxValue) {
                vOriginal = maxValue;
            }
        }

        const truncatedValue = truncateDecimals(vOriginal, 2);

        setFieldValue(`dates.${index}.valueOriginal`, toFullPrecisionString(vOriginal));
        setFieldValue(`dates.${index}.value`, truncatedValue);
        if (hasTotalValue) {
            const percentFromValue = (vOriginal / totalValue) * 100;
            setFieldValue(`dates.${index}.percOriginal`, toFullPrecisionString(percentFromValue));
            setFieldValue(`dates.${index}.perc`, truncateDecimals(percentFromValue, 2));
        }

        setFieldValue(`dates.${index}.isUserEdited`, true);
    };

    return (
        <tr ref={provided?.innerRef} {...(provided ? provided.draggableProps : {})}>
            <td className="tw-p-2 tw-align-middle tw-w-8">
                <i
                    {...(provided ? provided.dragHandleProps : {})}
                    className="fas fa-grip-vertical tw-text-gray-400 hover:tw-text-gray-600 tw-cursor-move"
                    title={$t('Drag to reorder')}
                />
            </td>
            <td className="tw-p-2 tw-align-middle tw-w-1/4">
                <DatePickerField
                    name={`dates.${index}.date`}
                    valueFormat="yyyy-MM-dd"
                    dateFormat="yyyy-MM-dd"
                    className={
                        errors?.date && touched?.date
                            ? `text-input !tw-bg-grey/50 tw-border tw-border-red-500 tmpl-input-${section}`
                            : `text-input !tw-bg-grey/50 tw-border-0 tmpl-input-${section}`
                    }
                    value={values?.dates[index]?.date}
                    onChange={setFieldValue}
                    min={minDate}
                    max={maxDate}
                    // Portalled because every screen rendering this row places the table inside a
                    // position-relative, overflow-hidden card. Rendered in place, the calendar is clipped at
                    // the card edge and some weeks or the month navigation become unreachable.
                    portalId="milestone-datepicker-portal"
                    // Popper does not reposition when the calendar's own content grows. A fixed six-week
                    // height stops a later month change from growing it past the viewport edge.
                    fixedHeight
                    // The portalled calendar follows its input on scroll and would sit over the sticky
                    // page header. The page scrolls inside a container, which the boolean form ignores.
                    // Only applied while open: the picker's scroll listener otherwise blurs a closed input.
                    onCalendarOpen={() => setIsCalendarOpen(true)}
                    onCalendarClose={() => setIsCalendarOpen(false)}
                    closeOnScroll={isCalendarOpen ? closeOnContainerScroll : false}
                />
                {errors?.date && touched?.date && (
                    <div className="tw-text-red-500 tw-text-xs tw-mt-1">{Array.isArray(errors.date) ? errors.date.join(' ') : errors.date}</div>
                )}
            </td>
            <td className="tw-p-2 tw-align-middle tw-w-1/4">
                <Field
                    type="text"
                    className={errors?.name && touched?.name ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'}
                    name={`dates.${index}.name`}
                    placeholder={$t('Description')}
                />
                {errors?.name && touched?.name && (
                    <div className="tw-text-red-500 tw-text-xs tw-mt-1">{Array.isArray(errors.name) ? errors.name.join(' ') : errors.name}</div>
                )}
            </td>
            <td className="tw-p-2 tw-align-middle tw-w-1/4">
                {isSliderAllowed ? (
                    <Slider
                        key={values?.dates[index]?.uniqueKey}
                        index={index}
                        defaultValue={values?.dates[index]?.perc}
                        max={maxPerc}
                        setFieldValue={setFieldValue}
                        name={`dates.${index}.perc`}
                    ></Slider>
                ) : (
                    <NumberFormat
                        value={values?.dates[index]?.perc ?? ''}
                        className={errors?.perc && touched?.perc ? 'tw-border tw-border-red-500 text-input tw-w-full' : 'tw-border-0 text-input tw-w-full'}
                        decimalScale={2}
                        allowNegative={false}
                        onValueChange={handleTypedNumber(handlePercentChange)}
                        placeholder="0.00"
                    />
                )}
                {errors?.perc && touched?.perc && (
                    <div className="tw-text-red-500 tw-text-xs tw-mt-1">{Array.isArray(errors.perc) ? errors.perc.join(' ') : errors.perc}</div>
                )}
            </td>
            <td className="tw-p-2 tw-align-middle tw-text-right tw-w-1/4">
                <NumberFormat
                    value={computedAmount}
                    className="tw-text-right tw-border-0 text-input tw-w-full"
                    decimalScale={2}
                    allowNegative={false}
                    thousandSeparator={true}
                    onValueChange={handleTypedNumber(handleValueChange)}
                    placeholder="0.00"
                />
            </td>
            {showActions && <td className="tw-p-2 tw-align-middle tw-w-8">{actions}</td>}
        </tr>
    );
}
